<h2 mat-dialog-title>{{ dialogTitle }}</h2>

<mat-dialog-content [formGroup]="form">
	<mat-form-field class="full">
		<mat-label>Employé</mat-label>
		<mat-select formControlName="id_employee">
			@for (e of data.employees; track e.id) {
				<mat-option [value]="e.id">{{ e.name }}</mat-option>
			}
		</mat-select>
	</mat-form-field>

	<mat-form-field class="full">
		<mat-label>Type d'absence</mat-label>
		<mat-select formControlName="typ">
			@for (t of data.types; track t.id) {
				<mat-option [value]="t.id">{{ t.name }}</mat-option>
			}
		</mat-select>
	</mat-form-field>

	<div class="duration">
		<span class="field-label">Durée</span>
		<mat-radio-group formControlName="duration_kind" (change)="onDurationKindChange(form.get('duration_kind')!.value!)">
			<mat-radio-button [value]="DURATION_DAY">Journée</mat-radio-button>
			<mat-radio-button [value]="DURATION_HALF">Demi-journée</mat-radio-button>
			<mat-radio-button [value]="DURATION_CUSTOM">Personnaliser</mat-radio-button>
		</mat-radio-group>
	</div>

	<!-- Demi-journée : LAQUELLE. C'est le sens même de « je pars récupérer cet après-midi » —
	     et c'est cette moitié-là, et elle seule, que le planning masquera. -->
	@if (form.get('duration_kind')!.value === DURATION_HALF) {
		<div class="duration">
			<span class="field-label">Moitié de journée</span>
			<mat-radio-group formControlName="half">
				<mat-radio-button value="morning">Matin</mat-radio-button>
				<mat-radio-button value="afternoon">Après-midi</mat-radio-button>
			</mat-radio-group>
		</div>
	}

	@if (form.get('duration_kind')!.value === DURATION_CUSTOM) {
		<mat-form-field class="hours">
			<mat-label>Nombre d'heures</mat-label>
			<input matInput type="number" min="0.5" step="0.5" formControlName="hours">
			<span matTextSuffix>h</span>
		</mat-form-field>
	}

	<!-- UNE SEULE SAISIE POUR LA PÉRIODE. Deux calendriers indépendants laissaient poser une fin
	     AVANT le début : la saisie était acceptée, le décompte affichait « — », et le refus ne
	     tombait qu'au moment d'envoyer. Avec la plage, l'inversion n'existe plus : le premier clic
	     pose le début, le second la fin, et cliquer avant le début recommence la sélection là.
	     On voit la période se remplir pendant qu'on survole — c'est le geste des sites de voyage.

	     Le calendrier redevient une DATE UNIQUE quand la demande ne peut porter que sur un jour
	     (demi-journée) ou quand sa fin n'est pas connue : une plage dont les deux bouts doivent
	     être égaux ne veut rien dire. -->
	<div class="flexrow dates">
		@if (singleDate) {
			<mat-form-field class="one-date">
				<mat-label>{{ form.get('openEnd')!.value ? 'À partir du' : 'Le' }}</mat-label>
				<input matInput [matDatepicker]="dayPicker" formControlName="datestart" placeholder="JJ/MM/AAAA">
				<mat-datepicker-toggle matIconSuffix [for]="dayPicker"></mat-datepicker-toggle>
				<mat-datepicker #dayPicker></mat-datepicker>
			</mat-form-field>
			@if (form.get('openEnd')!.value) {
				<span class="days-chip open">en cours — fin inconnue</span>
			}
		} @else {
			<mat-form-field class="range">
				<mat-label>Période</mat-label>
				<mat-date-range-input [rangePicker]="rangePicker">
					<input matStartDate formControlName="datestart" placeholder="Du">
					<input matEndDate formControlName="dateend" placeholder="au (inclus)">
				</mat-date-range-input>
				<mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
				<mat-date-range-picker #rangePicker></mat-date-range-picker>
			</mat-form-field>
			@if (days > 0) {
				<span class="days-chip">{{ days }} jour{{ days > 1 ? 's' : '' }}</span>
			}
		}
	</div>

	<!-- ABSENCE EN COURS : « il n'est pas là ce matin, je ne sais ni s'il revient ni quand ».
	     Elle court jusqu'à aujourd'hui, jour après jour, jusqu'à ce que l'arrêt de travail arrive
	     et qu'on la clôture — c'est la seule façon honnête de noter ce qu'on ne sait pas encore. -->
	@if (data.isAdmin || form.get('openEnd')!.value) {
		<mat-checkbox formControlName="openEnd" (change)="onOpenEndChange($event.checked)">
			Fin inconnue — l'absence court jusqu'à sa clôture
		</mat-checkbox>
	}

	<!-- Repos hebdomadaires : compris dans la plage mais NON décomptés du solde. -->
	<div class="rest">
		<span class="field-label" matTooltip="Ces jours sont compris dans la période mais ne sont pas décomptés du solde d'absence">
			Repos hebdomadaires
		</span>
		<div class="rest-chips">
			@for (c of dayChips; track c.day) {
				<button type="button" class="rest-chip" [class.on]="restDays().has(c.day)"
					(click)="toggleRestDay(c.day)">{{ c.label }}</button>
			}
		</div>
	</div>

	<div class="breakdown" [class.empty]="breakdown().total_days === 0">{{ breakdownLabel() }}</div>

	<!-- RÉCUPÉRATION : le solde d'heures supplémentaires mises en repos, et ce que la demande
	     en retire. Le salarié voit ce qu'il lui restera AVANT d'envoyer. -->
	@if (isRcr()) {
		@if (rcrAccount(); as account) {
			<div class="rcr" [class.insufficient]="!!rcrIssue()">
				<div class="rcr-line">
					<mat-icon>history_toggle_off</mat-icon>
					<span class="rcr-title">Compteur de récupération</span>
					<span class="rcr-available">{{ rcrLabel(account.available_min) }} disponible</span>
				</div>
				<div class="rcr-detail">
					{{ rcrLabel(account.credited_min) }} acquis
					@if (account.taken_min > 0) { · {{ rcrLabel(account.taken_min) }} déjà pris }
					@if (account.pending_min > 0) { · {{ rcrLabel(account.pending_min) }} en attente de réponse }
				</div>
				@if (rcrCost() > 0) {
					<div class="rcr-detail">
						Cette demande retire <b>{{ rcrLabel(rcrCost()) }}</b> du planning
						@if (!rcrIssue()) { — il resterait {{ rcrLabel(account.available_min - rcrCost()) }}. }
					</div>
				}
				@if (rcrIssue()) {
					<div class="rcr-issue">{{ rcrIssue() }}</div>
				}
			</div>
		} @else {
			<div class="rcr">
				<div class="rcr-detail">Compteur de récupération indisponible : la demande part sans contrôle de solde.</div>
			</div>
		}
	}

	<!-- CONGÉ PLAFONNÉ : ce que la convention accorde à l'unité (« un jour rémunéré pour enfant
	     malade par année civile »). Le droit se voit AVANT l'envoi, jamais dans un refus. -->
	@if (quotaAccount(); as account) {
		<div class="quota" [class.insufficient]="!!quotaIssue()">
			<div class="quota-line">
				<mat-icon>event_available</mat-icon>
				<span class="quota-title">{{ typeName() }}</span>
				<span class="quota-available">
					{{ quotaDaysLabel(account.remaining_days) }} sur {{ quotaDaysLabel(account.quota.days) }}
				</span>
			</div>
			<div class="quota-detail">
				Droit {{ quotaPeriodLabel(account.period) }}
				@if (account.used_days > 0) { · {{ quotaDaysLabel(account.used_days) }} déjà pris }
				@if (account.pending_days > 0) { · {{ quotaDaysLabel(account.pending_days) }} en attente de réponse }
				@if (account.eligible_from && account.eligible) { · droit ouvert depuis le {{ frDat(account.eligible_from) }} }
			</div>
			@if (quotaIssue()) {
				<div class="quota-issue">{{ quotaIssue() }}</div>
			}
		</div>
	}

	<!-- Congé principal posé hors de la période légale : la loi accorde des jours en plus
	     (L3141-23), auxquels le salarié peut renoncer par accord individuel (L3141-21). -->
	@if (fractionationDays() > 0) {
		<div class="fractionation" [class.blocking]="!!waiverIssue()">
			<span class="fractionation-title">
				{{ outOfTakingPeriod() }} jour(s) hors de la période de congés ({{ takingPeriodLabel() }})
			</span>
			<span class="fractionation-detail">
				Ce fractionnement ouvre droit à <b>+{{ fractionationDays() }} jour(s) ouvrable(s)</b>
				de congés supplémentaires.
			</span>
			<!-- La case est là dès que l'établissement propose la renonciation OU l'exige. Elle ne
			     dépend plus de l'ATTRIBUTION des jours : c'est quand on ne les attribue pas que la
			     renonciation devient indispensable — et si elle bloque la demande, la retirer
			     interdirait pour de bon de poser un congé hors période. -->
			@if (waiverOffered()) {
				<mat-checkbox formControlName="fractionationWaiver">
					Le salarié renonce à ces jours de fractionnement
				</mat-checkbox>
				<span class="fractionation-detail">
					La renonciation est enregistrée avec son auteur et sa date : elle vaut accord individuel.
					@if (leaveRules().fractionation_waiver_blocking) {
						<b>Sans elle, la demande ne peut pas être enregistrée.</b>
					} @else if (leaveRules().fractionation_enabled) {
						Sans elle, les jours sont ajoutés au solde.
					} @else {
						L'établissement n'attribue pas ces jours : c'est elle qui rend la pratique régulière.
					}
				</span>
			}
		</div>
	}

	<mat-form-field class="full">
		<mat-label>Commentaire</mat-label>
		<input matInput formControlName="comment">
	</mat-form-field>

	<!-- LA SAISIE DU RESPONSABLE RH VAUT DÉCISION : il ne s'adresse pas une demande à lui-même. La
	     case « Approuver immédiatement » n'avait donc rien à décider — décochée, elle déposait dans
	     « À traiter » une ligne que son propre auteur devait revalider. Reste la mention, pour que
	     l'état de la ligne enregistrée ne soit pas une surprise.
	     La REQUALIFICATION garde la case : là, une demande existe bel et bien, et la rendre au
	     salarié sans la trancher est une réponse légitime. -->
	@if (isDirectEntry) {
		<div class="direct-entry">
			<mat-icon>verified</mat-icon>
			<span>Enregistrée <b>validée</b> — votre saisie vaut décision.</span>
		</div>
	} @else if (data.isAdmin && isRequalification) {
		<mat-checkbox formControlName="approveNow">
			Approuver dans la foulée (la requalification vaut décision)
		</mat-checkbox>
	}

	@if (errorMessage) {
		<div class="error">{{ errorMessage }}</div>
	}
</mat-dialog-content>

<mat-dialog-actions align="end">
	<button mat-button type="button" (click)="onCancel()">Annuler</button>
	<button mat-flat-button color="primary" type="button" (click)="onValidate()">{{ validateLabel }}</button>
</mat-dialog-actions>
